import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { delayWhen, Observable, retryWhen, shareReplay, Subject, timer } from 'rxjs';
import { Broadcast } from '../../../models/broadcast';

export const QUERY_BROADCASTS = gql`
query broadcasts($mode: String!, $date: DateTime!, $channel: String!) {
          broadcasts(mode: $mode, date: $date, channel: $channel) {
            key
            createdAt
            title
            subtitle
            startTime
            bruttoDuration
            ddsStartsAt
            ddsEndsAt
            ddsIntervals {
              startsAt
              endsAt
            }
            channel
            vpsCode
            live
            redsysSendungsname
            editors
            wavTranscriptCreatedAt
            wavTranscriptRearrangedCreatedAt
            wavKey
            editorialNoteKey
            editorialNoteEditors
            transcribeLiveServiceIP
            ffmpegStartedAt
            ffmpegStoppedAt
            recordingStartedAt
            recordingStoppedAt
            kiut
            liveStreamSource
            isPcmsDownloaded
            isPretranscribed
            pcmsUrl
            pcmsSizeInMb
            pcmsVideoDurationSecs
            audioDuration
            pcmsStatus
            pcmsImportDate
            pcmsFabStlUploadDate
            transcriptWordsCount
            transcriptSegmentsCount
            transcriptDuration
            transcriptModifiedDate
            liveStreamSource
            voxtralMini4bRealtimeEc2IP
            voxtralMini4bRealtimeSessionId
            voxtralMini4bRealtimeEc2StartedAt
            voxtralMini4bRealtimeEc2StoppedAt
            voxtralMini4bRealtimeSessionStartedAt
            voxtralMini4bRealtimeSessionStoppedAt
            voxtralMini4bRealtimeStalledSince
            voxtralMini4bRealtimeFabEnabled
            voxtralMini4bRealtimeFabUrl
            voxtralMini4bRealtimeFabMode
            # 2026-09-02 — persisted since 2026-07-02 but never selected here,
            # so every row reached the UI undefined and both consumers read
            # that as true. The operator untick of "FAB nach DDS Start und Stop
            # aktivieren" therefore came back ticked on the next panel open
            # (PRD LIVE/2026/0902 1055/ORFS). Same story for
            # RestartAfterBroadcast in the schedule dialog.
            voxtralMini4bRealtimeIsFabDdsStartStopActive
            voxtralMini4bRealtimeRestartAfterBroadcast
            voxtralMini4bRealtimeDiarizationEnabled
            voxtralMini4bRealtimeCorrectionContext
            voxtralMini4bRealtimeCorrectionEnabled
            voxtralMini4bRealtimeCorrectionMode
            voxtralMini4bRealtimeScheduled
            voxtralMini4bRealtimeOperatorStoppedAt
            # 2026-10-09 — S3 mirror of the session export (download + "Segmente in S3").
            voxtralMini4bRealtimeSegmentsS3Key
            voxtralMini4bRealtimeSegmentsCount
          }
        }`;

export const BROADCASTS_SUBSCRIPTION = gql`
  subscription {
    onBroadcastsChanged {
      key
      createdAt
      title
      subtitle
      startTime
      bruttoDuration
      ddsStartsAt
      ddsEndsAt
      ddsIntervals {
        startsAt
        endsAt
      }
      channel
      vpsCode
      live
      redsysSendungsname
      editors
      wavTranscriptCreatedAt
      wavTranscriptRearrangedCreatedAt
      wavKey
      editorialNoteKey
      editorialNoteEditors
      transcribeLiveServiceIP
      ffmpegStartedAt
      ffmpegStoppedAt
      recordingStartedAt
      recordingStoppedAt
      kiut
      isPcmsDownloaded
      isPretranscribed
      pcmsUrl
      pcmsSizeInMb
      pcmsVideoDurationSecs
      audioDuration
      pcmsStatus
      pcmsImportDate
      pcmsFabStlUploadDate
      transcriptWordsCount
      transcriptSegmentsCount
      transcriptDuration
      transcriptModifiedDate
      liveStreamSource
      voxtralMini4bRealtimeEc2IP
      voxtralMini4bRealtimeSessionId
      voxtralMini4bRealtimeEc2StartedAt
      voxtralMini4bRealtimeEc2StoppedAt
      voxtralMini4bRealtimeSessionStartedAt
      voxtralMini4bRealtimeSessionStoppedAt
      voxtralMini4bRealtimeStalledSince
      voxtralMini4bRealtimeFabEnabled
      voxtralMini4bRealtimeFabUrl
      voxtralMini4bRealtimeFabMode
      # Must mirror QUERY_BROADCASTS — a field missing here silently reverts
      # to undefined on the first live push after an operator change.
      voxtralMini4bRealtimeIsFabDdsStartStopActive
      voxtralMini4bRealtimeRestartAfterBroadcast
      voxtralMini4bRealtimeDiarizationEnabled
      voxtralMini4bRealtimeCorrectionContext
      voxtralMini4bRealtimeCorrectionEnabled
      voxtralMini4bRealtimeCorrectionMode
      voxtralMini4bRealtimeScheduled
      voxtralMini4bRealtimeOperatorStoppedAt
      # 2026-10-09 — S3 mirror of the session export (download + "Segmente in S3").
      voxtralMini4bRealtimeSegmentsS3Key
      voxtralMini4bRealtimeSegmentsCount
      deleted
    }
  }
`;

export const GET_PRESIGNED_URL_FOR_GET = gql`
  query GetPresignedUrlForGet($key: String!) {
    downloadURL(key: $key)
  }
`;

export const GET_PRESIGNED_URL_FOR_PUT = gql`
  query GetPresignedUrlForPut($key: String!) {
    uploadURL(key: $key)
  }
`;

const DELETE_ARCHIVE_BROADCAST = gql`
  mutation DeleteArchiveBroadcast($key: String!) {
    deleteArchiveBroadcast(key: $key)
  }
`;

const QUERY_ARCHIVE_BROADCASTS = gql`
  query archiveBroadcasts($limit: Int!, $nextToken: String, $search: String) {
    archiveBroadcasts(limit: $limit, nextToken: $nextToken, search: $search) {
      items {
        key
        createdAt
        title
        episodeId
        bruttoDuration
        editors
        wavKey
        wavTranscriptCreatedAt
        wavTranscriptRearrangedCreatedAt
        audioDuration
        pcmsStatus
        transcriptModifiedDate
      }
      totalCount
      nextToken
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class BroadcastsService {

  private apolloHot: ApolloBase;
  private broadcastsSubject = new Subject<any>();
  private isConnected = false;

  /**
   * Emits once when the subscription delivers data again after an
   * error/reconnect. The backend publisher advances its change cursor
   * globally, so events sent during this client's WebSocket outage are lost
   * for it — consumers should reload their day on this signal.
   */
  readonly connectionRestored$ = new Subject<void>();
  private hadSubscriptionError = false;

  constructor(private apollo: Apollo) {
    this.apolloHot = this.apollo.use('hotchocolate');
    this.initSubscription();
  }

  getData(mode: string, date: Date, channel: string = ''): Observable<ApolloQueryResult<any>> {
    console.log('Lade Daten für:', date, mode, channel);
    return this.apolloHot.query({
      query: QUERY_BROADCASTS,
      variables: {
        mode: mode,
        date: date,
        channel: channel
      },
      fetchPolicy: 'network-only'
    });
  }

  private initSubscription(): void {
    const connect = () => {
      console.log('🚀 Starte Apollo Subscription');

      const sub = this.apolloHot.subscribe({
        query: BROADCASTS_SUBSCRIPTION
      }).subscribe({
        next: data => {
          console.log('📦 Empfangen:', (data as { data: { onBroadcastsChanged: Broadcast } })?.data?.onBroadcastsChanged?.key, data);
          if (this.hadSubscriptionError) {
            this.hadSubscriptionError = false;
            this.connectionRestored$.next();
          }
          this.broadcastsSubject.next(data);
        },
        error: err => {
          console.error('❌ Fehler bei Apollo Subscription:', err);
          this.hadSubscriptionError = true;
          sub.unsubscribe();
          reconnect(); // sofort wieder verbinden
        },
        complete: () => {
          console.warn('⚠️ Subscription wurde beendet');
          reconnect(); // ggf. wieder verbinden
        }
      });
    };

    const reconnect = () => {
      const delay = 2000 + Math.random() * 2000; // bis zu 4s zufällig
      console.log(`🔁 Reconnect in ${delay}ms`);
      setTimeout(() => connect(), delay);
    };

    connect(); // Initialer Verbindungsaufbau
  }

  subscribeToBroadcasts(): Observable<any> {
    return this.broadcastsSubject.asObservable();
  }

  getPresignedUrlForGet(key: string): Observable<any> {
    return this.apolloHot.query({
      query: GET_PRESIGNED_URL_FOR_GET,
      variables: { key },
      fetchPolicy: 'network-only'
    });
  }

  getPresignedUrlForPut(key: string): Observable<any> {
    return this.apolloHot.query({
      query: GET_PRESIGNED_URL_FOR_PUT,
      variables: { key },
      fetchPolicy: 'network-only'
    });
  }

  deleteArchiveBroadcast(key: string): Observable<any> {
    return this.apolloHot.mutate({
      mutation: DELETE_ARCHIVE_BROADCAST,
      variables: { key }
    });
  }

  getArchiveBroadcasts(limit: number, nextToken?: string | null, search?: string): Observable<ApolloQueryResult<any>> {
    return this.apolloHot.query({
      query: QUERY_ARCHIVE_BROADCASTS,
      variables: { limit, nextToken: nextToken || null, search: search || null },
      fetchPolicy: 'network-only'
    });
  }
}
